import * as React from "react";
import { ArrowUpDownIcon, ChevronDownIcon, SearchIcon } from "lucide-react";

import { cn } from "@/lib/utils";

import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "./dropdown-menu";
import { FilterChip } from "./filter-chip";

/** The `ListPage` toolbar row under the `PageHeader`: search, sort, chips, actions. */
function ListToolbar({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="list-toolbar"
      className={cn("flex min-w-0 flex-wrap items-center gap-2", className)}
      {...props}
    />
  );
}

type ListSearchProps = Omit<React.ComponentProps<"input">, "onChange"> & {
  value: string;
  onValueChange: (value: string) => void;
};

/** A 28px search field with a leading icon. */
function ListSearch({
  value,
  onValueChange,
  className,
  placeholder = "Search",
  id,
  ...props
}: ListSearchProps) {
  const fallbackId = React.useId();
  const inputId = id ?? fallbackId;
  return (
    <label
      htmlFor={inputId}
      data-slot="list-search"
      className={cn(
        "flex h-7 w-60 min-w-30 items-center gap-1.5 rounded-md px-2 text-fg-3 inset-ring inset-ring-line-3 focus-within:inset-ring-accent-line",
        className
      )}
    >
      <SearchIcon className="size-3.5 shrink-0" aria-hidden />
      <input
        id={inputId}
        type="search"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent text-base text-fg outline-none placeholder:text-fg-4"
        {...props}
      />
    </label>
  );
}

type MenuOption = { value: string; label: React.ReactNode };

type SortMenuProps = {
  options: MenuOption[];
  value: string;
  onValueChange: (value: string) => void;
  /** Shown on the trigger; defaults to the selected option's label. */
  label?: React.ReactNode;
};

/** A ghost "Sort" trigger with a radio menu. */
function SortMenu({ options, value, onValueChange, label }: SortMenuProps) {
  const selected = options.find((o) => o.value === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="Sort" className="gap-1.5">
          <ArrowUpDownIcon />
          <span className="hidden md:inline">
            {label ?? selected?.label ?? "Sort"}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Sort By</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={value} onValueChange={onValueChange}>
          {options.map((o) => (
            <DropdownMenuRadioItem key={o.value} value={o.value}>
              {o.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type ChipMenuProps = {
  /** "Status", "Visa Type", "Assigned". */
  label: string;
  options: MenuOption[];
  /** `""` means any. */
  value: string;
  onValueChange: (value: string) => void;
  /** First option, selecting it clears the filter ("Any Status"). */
  anyLabel?: string;
  icon?: React.ReactNode;
  /** Long lists scroll inside the menu. */
  scroll?: boolean;
};

/** A `FilterChip` that opens a radio menu; shows "Label: Value" while active. */
function ChipMenu({
  label,
  options,
  value,
  onValueChange,
  anyLabel = "Any",
  icon,
  scroll = false
}: ChipMenuProps) {
  const selected = options.find((o) => o.value === value);
  const active = Boolean(value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <FilterChip
          active={active}
          onClear={() => onValueChange("")}
          aria-label={`Filter by ${label}`}
        >
          {icon}
          <span>{label}</span>
          {active && selected ? (
            <span className="max-w-40 truncate text-fg">{selected.label}</span>
          ) : (
            <ChevronDownIcon className="text-fg-4" />
          )}
        </FilterChip>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuRadioGroup value={value} onValueChange={onValueChange}>
          <DropdownMenuRadioItem value="">{anyLabel}</DropdownMenuRadioItem>
          <DropdownMenuSeparator />
          <div className={cn(scroll && "max-h-60 overflow-y-auto")}>
            {options.map((o) => (
              <DropdownMenuRadioItem key={o.value} value={o.value}>
                {o.label}
              </DropdownMenuRadioItem>
            ))}
          </div>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { ChipMenu, ListSearch, ListToolbar, SortMenu };
export type { ChipMenuProps, ListSearchProps, MenuOption, SortMenuProps };
